import { getPositionPermissionScope, POSITION_PERMISSION_GRANTED_EVENT } from '../../services/PositionSnapshotService';
import { isForeground } from '../../services/AppStateService';
import { beginCoachFlow, bindCoachFlowSubject, failCoachFlow, markCoachFlow } from '../../services/coachSequenceTiming';

const gpsError = code => {
  const message = ({
    1: 'Location permission was denied. Allow location for this app in your device settings, then submit again.',
    2: 'A GPS position is unavailable right now. Move somewhere with a clearer signal and submit again.',
    3: 'Getting location timed out. Move somewhere with a clearer signal and submit again.',
  })[code] || 'Could not get a fresh GPS position. Please submit again.';
  return Object.assign(new Error(`${message} Your activity remains pending.`), { code: 'COMPLETION_GPS_UNAVAILABLE', deviceCode: code });
};

export const getFreshGpsPosition = async (geolocation = navigator.geolocation, { signalPermission = true } = {}) => {
  const permissionScope = getPositionPermissionScope();
  if (!geolocation?.getCurrentPosition) {
    throw new Error('GPS is unavailable on this device. Your activity remains pending.');
  }
  let fix;
  try {
    fix = await new Promise((resolve, reject) => {
      // Bound the JS wait too: some native bridges never deliver a callback.
      const timer = setTimeout(() => reject({ code: 3 }), 15000);
      const done = callback => value => { clearTimeout(timer); callback(value); };
      try {
        geolocation.getCurrentPosition(done(resolve), done(reject), {
          enableHighAccuracy: true, maximumAge: 0, timeout: 15000,
        });
      } catch (error) { clearTimeout(timer); reject(error); }
    });
  } catch (error) {
    throw gpsError(error?.code);
  }
  const position = {
    latitude: fix?.coords?.latitude, longitude: fix?.coords?.longitude,
    accuracy: fix?.coords?.accuracy, source: 'gps',
    captured_at: Number.isFinite(fix?.timestamp) ? new Date(fix.timestamp).toISOString() : null,
  };
  if (!getCachedCompletionPosition({ getEventPosition: () => position })) {
    throw new Error('GPS did not provide a valid fresh position. Please submit again. Your activity remains pending.');
  }
  // Diagnostics keep the existing lifecycle-scoped permission evidence. Submit
  // defers this event until fresh consent and its original scope are rechecked.
  if (signalPermission && permissionScope && permissionScope === getPositionPermissionScope()) {
    window.dispatchEvent(new CustomEvent(POSITION_PERMISSION_GRANTED_EVENT, { detail: { scope: permissionScope } }));
  }
  return position;
};

export const captureActivityPosition = async (backendService, activityId, geolocation = navigator.geolocation) =>
  backendService.setActivityPosition(activityId, await getFreshGpsPosition(geolocation));

export const getCachedCompletionPosition = backendService => {
  const position = backendService.getEventPosition?.();
  const timestamp = Date.parse(position?.captured_at);
  const age = Date.now() - timestamp;
  if (!position || position.source !== 'gps' || !Number.isFinite(timestamp) || age > 30000 || age < -5000 ||
      !Number.isFinite(position.latitude) || Math.abs(position.latitude) > 90 ||
      !Number.isFinite(position.longitude) || Math.abs(position.longitude) > 180 ||
      !Number.isFinite(position.accuracy) || position.accuracy <= 0 || position.accuracy > 10000 ||
      new Date(timestamp).toISOString() !== position.captured_at) return null;
  return { ...position };
};

// Only explicit foreground completion callers enter this seam. Never move GPS
// acquisition into finishActivity/interceptors, outbox replay or automatic status.
const completionIntents = new WeakMap();
export const bindCompletionContext = backendService => ({
  snapshot: getCachedCompletionPosition(backendService),
  owner: backendService.positionOwner,
  identity: backendService.authenticatedIdentity,
  permissionScope: getPositionPermissionScope(),
  runtime: backendService.positionRuntime,
  epoch: backendService.getLocationConsentEpoch?.(),
});
export const finishWithPosition = (backendService, activityId, geolocation = navigator.geolocation, options = {}) => {
  let intents = completionIntents.get(backendService);
  if (!intents) { intents = new Map(); completionIntents.set(backendService, intents); }
  if (intents.has(activityId)) return intents.get(activityId);

  // Bind all authority synchronously, before even workout-outbox drainage.
  const context = options.initiatingContext || bindCompletionContext(backendService);
  let snapshot = context.snapshot;
  const { owner, identity, permissionScope, runtime, epoch } = context;
  let enabled = false;
  let devicePermission = null;
  const assertCurrent = () => {
    if ((backendService.getLocationConsentEpoch && !owner) || (permissionScope && permissionScope.owner !== owner)) {
      throw new Error('The initiating account owner is unavailable. Your activity remains pending.');
    }
    if (owner !== backendService.positionOwner || identity !== backendService.authenticatedIdentity ||
        permissionScope !== getPositionPermissionScope() || runtime !== backendService.positionRuntime ||
        context.signal?.aborted || options.signal?.aborted || options.isCurrent?.() === false || !isForeground()) {
      throw Object.assign(new Error('Activity submission changed or was cancelled. Your activity remains pending.'), { code: 'REQUEST_ACCOUNT_CHANGED' });
    }
    if (epoch === null || epoch !== backendService.getLocationConsentEpoch?.()) {
      throw new Error('Position consent changed during submission. Your activity remains pending.');
    }
    if (enabled && devicePermission?.state === 'denied') throw gpsError(1);
  };
  const assertPosition = () => {
    assertCurrent();
    if (enabled && (!getCachedCompletionPosition({ getEventPosition: () => snapshot }) ||
        (owner && snapshot?.owner_user_id !== owner))) {
      throw new Error('The initiating GPS position is no longer fresh. Please submit again. Your activity remains pending.');
    }
  };
  const preflight = async () => {
    assertCurrent();
    options.onStage?.('checking');
    // The service converges on fresh same-scope reads without retry GET waves.
    const user = await backendService.fetchUserProfile({ shareInFlight: false, requireAuthoritative: true });
    assertCurrent();
    if (!user || (owner && user._id && String(user._id) !== String(owner))) {
      throw new Error('Could not check current position consent. Your activity remains pending.');
    }
    const dojo = await backendService.getMyDojo();
    assertCurrent();
    const rules = dojo?._id ? await backendService.getDojoRequirements(dojo._id) : null;
    assertCurrent();
    if (dojo?._id && !rules?.requirements) {
      throw new Error('Could not check dojo GPS requirements. Reconnect and try again; your activity remains pending.');
    }
    if (rules?.requirements?.location_capture_enabled === true && user.location_capture_enabled !== true) {
      throw new Error('This dojo requires fresh GPS. Enable Capture position when filing activities in your profile before completing this activity. Your activity remains pending.');
    }
    return user.location_capture_enabled === true;
  };
  const coachFlow = beginCoachFlow('activity_review', typeof window !== 'undefined' ? window.event?.timeStamp : undefined);
  bindCoachFlowSubject(coachFlow, String(activityId));
  const pending = (async () => {
    markCoachFlow(coachFlow, 'presubmit_start');
    markCoachFlow(coachFlow, 'profile_start');
    enabled = await preflight();
    markCoachFlow(coachFlow, 'profile_end');
    if (options.beforeCompletion) { await options.beforeCompletion(); assertCurrent(); }
    if (enabled && !snapshot) {
      if (options.acquirePosition === false) {
        throw new Error('Fresh GPS is not ready. Submit this activity to get location. Your activity remains pending.');
      }
      // This is a new on-demand acquisition boundary, not a replacement for a
      // valid initiating fix. A fresh consent read authorizes this gesture only.
      enabled = await preflight();
      if (!enabled) throw new Error('Position consent changed during submission. Your activity remains pending.');
      if (navigator.permissions?.query) {
        try { devicePermission = await navigator.permissions.query({ name: 'geolocation' }); } catch (_) { /* explicit gesture may ask */ }
        assertCurrent();
      }
      options.onGettingLocation?.(true);
      try {
        options.onStage?.('location');
        markCoachFlow(coachFlow, 'gps_start');
        const position = await getFreshGpsPosition(geolocation, { signalPermission: false });
        markCoachFlow(coachFlow, 'gps_end');
        assertCurrent();
        options.onGettingLocation?.(false);
        snapshot = { ...position, ...(owner ? { owner_user_id: owner } : {}) };
        if (!await preflight()) throw new Error('Position consent changed during submission. Your activity remains pending.');
        assertPosition();
        if (permissionScope && permissionScope.owner === owner) {
          window.dispatchEvent(new CustomEvent(POSITION_PERMISSION_GRANTED_EVENT, { detail: { scope: permissionScope } }));
        }
      } finally { options.onGettingLocation?.(false); }
    }
    if (!enabled) snapshot = null;
    assertPosition();
    const metadata = {
      katafitEventOwner: owner, katafitEventIdentity: identity,
      katafitCompletionIntent: { assertCurrent: assertPosition },
    };
    options.onStage?.('saving');
    markCoachFlow(coachFlow, 'presubmit_end');
    markCoachFlow(coachFlow, 'save_start');
    if (enabled) {
      const { owner_user_id, ...position } = snapshot;
      await backendService.setActivityPosition(activityId, position, snapshot, metadata);
      assertPosition();
    }
    try {
      const completed = await backendService.finishActivity(activityId, snapshot || null, metadata);
      markCoachFlow(coachFlow, 'save_end');
      return completed;
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.code === 'LOCATION_REQUIRED') {
        throw new Error(`${error.response.data.error || 'Fresh GPS position is required.'} Your activity remains pending.`);
      }
      throw error;
    }
  })();
  pending.catch(() => failCoachFlow(coachFlow));
  intents.set(activityId, pending);
  const clear = () => { if (intents.get(activityId) === pending) intents.delete(activityId); };
  pending.then(clear, clear);
  return pending;
};
